import { useEffect, useState } from "react";
import api from "../lib/api";
import { money, financialYear, currentQuarter, fmtDate, PAYMENT_METHODS } from "../lib/format";
import { Link } from "react-router-dom";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend, PieChart, Pie, Cell,
} from "recharts";
import {
  TrendingUp, TrendingDown, Wallet, FileText, ArrowRight, Landmark, ShieldCheck, AlertCircle,
} from "lucide-react";

const KPI = ({ icon: Icon, label, value, sub, tone }) => (
  <div className="kpi-card rounded-2xl bg-card border border-border p-5" data-testid={`kpi-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
    <div className="flex items-center justify-between">
      <span className="text-xs uppercase tracking-wider font-mono text-muted-foreground">{label}</span>
      <span className={`w-8 h-8 rounded-lg grid place-items-center ${tone}`}><Icon className="w-4 h-4" /></span>
    </div>
    <div className="num text-2xl font-bold mt-3 tracking-tight">{value}</div>
    {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
  </div>
);

export default function DashboardPage() {
  const [fy] = useState(financialYear());
  const [q] = useState(currentQuarter());
  const [summary, setSummary] = useState(null);
  const [bas, setBas] = useState(null);
  const [profile, setProfile] = useState(null);
  const [bank, setBank] = useState([]);
  const [runway, setRunway] = useState(null);
  const [cats, setCats] = useState([]);
  const [colorMap, setColorMap] = useState({});

  useEffect(() => {
    api.get("/reports/summary", { params: { date_from: fy.from, date_to: fy.to } }).then((r) => setSummary(r.data));
    api.get("/reports/bas", { params: { date_from: q.from, date_to: q.to } }).then((r) => setBas(r.data));
    api.get("/profile").then((r) => setProfile(r.data));
    api.get("/bank/transactions").then((r) => setBank(r.data));
    api.get("/reports/runway", { params: { date_from: fy.from, date_to: fy.to } }).then((r) => setRunway(r.data));
    api.get("/reports/pnl", { params: { date_from: fy.from, date_to: fy.to } }).then((r) => setCats(r.data.expense_by_category || []));
    api.get("/categories").then((r) => {
      const m = {};
      r.data.forEach((c) => { m[c.name] = c.color; });
      setColorMap(m);
    });
  }, [fy, q]);

  const PIE = ["#113E35", "#0284C7", "#059669", "#D97706", "#E11D48", "#7C3AED", "#0891B2", "#CA8A04"];

  const daysToBas = () => {
    const due = new Date(q.to);
    due.setDate(due.getDate() + 28);
    return Math.ceil((due - new Date()) / (1000 * 60 * 60 * 24));
  };
  const unreconciled = bank.filter((b) => !b.reconciled).length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">
            {profile?.business_name || "Dashboard"}
          </h1>
          <p className="text-muted-foreground text-sm mt-1 flex items-center gap-2">
            <span className="font-mono">{fy.label}</span> · ABN {profile?.abn || "—"}
          </p>
        </div>
        <Link to="/bas" data-testid="dashboard-bas-cta"
          className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2.5 text-sm font-medium hover:bg-primary/90 active:scale-[0.98] transition-all">
          <FileText className="w-4 h-4" /> Prepare BAS <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={TrendingUp} label="Revenue (ex GST)" value={money(summary?.net_income)} sub="Financial year to date" tone="bg-emerald-50 text-emerald-600" />
        <KPI icon={TrendingDown} label="Expenses (ex GST)" value={money(summary?.net_expense)} sub="Financial year to date" tone="bg-rose-50 text-rose-600" />
        <KPI icon={Wallet} label="Net Profit" value={money(summary?.net_profit)} sub={`${summary?.transaction_count || 0} transactions`} tone="bg-primary/10 text-primary" />
        <KPI icon={FileText} label="Net GST (this quarter)" value={money(bas?.amount)} sub={bas?.result === "payable" ? "Payable to ATO" : "Refund from ATO"} tone={bas?.result === "payable" ? "bg-rose-50 text-rose-600" : "bg-emerald-50 text-emerald-600"} />
      </div>

      {/* Alerts row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-2xl border border-border bg-card p-5 flex items-center gap-4" data-testid="bas-countdown">
          <div className="w-11 h-11 rounded-xl bg-amber-50 text-amber-600 grid place-items-center"><AlertCircle className="w-5 h-5" /></div>
          <div>
            <div className="text-sm font-semibold">{q.label} BAS</div>
            <div className="text-xs text-muted-foreground">Due in {daysToBas()} days (approx.)</div>
          </div>
        </div>
        <Link to="/bank" className="rounded-2xl border border-border bg-card p-5 flex items-center gap-4 hover:border-primary/40 transition-colors" data-testid="reconcile-alert">
          <div className="w-11 h-11 rounded-xl bg-sky-50 text-sky-600 grid place-items-center"><Landmark className="w-5 h-5" /></div>
          <div>
            <div className="text-sm font-semibold">{unreconciled} to reconcile</div>
            <div className="text-xs text-muted-foreground">Bank feed transactions</div>
          </div>
        </Link>
        <div className="rounded-2xl border border-border bg-card p-5 flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 grid place-items-center"><ShieldCheck className="w-5 h-5" /></div>
          <div>
            <div className="text-sm font-semibold">{profile?.reporting_period || "Quarterly"} reporting</div>
            <div className="text-xs text-muted-foreground capitalize">{profile?.accounting_method || "cash"} accounting basis</div>
          </div>
        </div>
      </div>

      {/* Cash runway */}
      {runway && (
        <div className="rounded-2xl border border-border bg-card p-5 flex flex-wrap items-center justify-between gap-4" data-testid="runway-card">
          <div className="flex items-center gap-4">
            <div className={`w-11 h-11 rounded-xl grid place-items-center ${runway.runway_months !== null && runway.runway_months < 3 ? "bg-rose-50 text-rose-600" : "bg-emerald-50 text-emerald-600"}`}>
              <Wallet className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider font-mono text-muted-foreground">Cash runway</div>
              <div className="num text-2xl font-bold mt-0.5" data-testid="runway-months">
                {runway.runway_months !== null ? `${runway.runway_months} months` : "—"}
              </div>
              <div className="text-xs text-muted-foreground">{money(runway.cash_balance)} on hand · {money(runway.avg_monthly_expense)}/mo avg spend</div>
            </div>
          </div>
          {runway.runway_months !== null && runway.runway_months < 3 && (
            <span className="text-xs px-3 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200 font-medium">Low runway — watch cash flow</span>
          )}
          {!runway.cash_balance && (
            <Link to="/settings" className="text-sm text-primary hover:underline">Set your cash balance →</Link>
          )}
        </div>
      )}

      {/* Charts */}
      {summary?.income_by_payment?.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="takings-by-payment">
          <h3 className="font-heading font-semibold">Takings by payment method</h3>
          <p className="text-xs text-muted-foreground mb-4">How your income was received (incl GST) · {fy.label}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {summary.income_by_payment.map((p) => (
              <div key={p.method} className="rounded-xl bg-secondary/60 px-4 py-3" data-testid={`takings-${p.method}`}>
                <div className="text-xs uppercase tracking-wider font-mono text-muted-foreground">
                  {PAYMENT_METHODS[p.method] || "Unspecified"}
                </div>
                <div className="num text-lg font-semibold mt-0.5">{money(p.amount)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {summary?.takings_trend?.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="takings-trend">
          <h3 className="font-heading font-semibold">Weekly takings — EFTPOS vs Cash</h3>
          <p className="text-xs text-muted-foreground mb-4">Spot your busiest weeks (incl GST) · last 8 weeks</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={summary.takings_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2DDD3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} stroke="#94A3B8" />
              <YAxis tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} stroke="#94A3B8" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 12, border: "1px solid #E2DDD3" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="eftpos" fill="#0284C7" name="EFTPOS" radius={[6, 6, 0, 0]} />
              <Bar dataKey="cash" fill="#059669" name="Cash" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {cats.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="expense-category-chart">
          <h3 className="font-heading font-semibold">Where the money goes</h3>
          <p className="text-xs text-muted-foreground mb-4">Expenses by category (ex GST) · {fy.label}</p>
          <div className="grid md:grid-cols-2 gap-4 items-center">
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={cats} dataKey="amount" nameKey="category" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2}>
                  {cats.map((c, i) => <Cell key={i} fill={colorMap[c.category] || PIE[i % PIE.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 12, border: "1px solid #E2DDD3" }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-1.5">
              {cats.slice(0, 8).map((c, i) => (
                <div key={c.category} className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 min-w-0"><span className="w-3 h-3 rounded-sm shrink-0" style={{ background: colorMap[c.category] || PIE[i % PIE.length] }} /><span className="truncate">{c.category}</span></span>
                  <span className="num text-muted-foreground">{money(c.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-2xl border border-border bg-card p-5">
          <h3 className="font-heading font-semibold">Cash flow (ex GST)</h3>
          <p className="text-xs text-muted-foreground mb-4">Monthly income vs expenses</p>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={summary?.cashflow || []} margin={{ left: -10, right: 10 }}>
              <defs>
                <linearGradient id="inc" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#059669" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#059669" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="exp" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#E11D48" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#E11D48" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2DDD3" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} stroke="#94A3B8" />
              <YAxis tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} stroke="#94A3B8" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 12, border: "1px solid #E2DDD3", fontFamily: "Inter" }} />
              <Area type="monotone" dataKey="income" stroke="#059669" strokeWidth={2} fill="url(#inc)" name="Income" />
              <Area type="monotone" dataKey="expense" stroke="#E11D48" strokeWidth={2} fill="url(#exp)" name="Expenses" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5">
          <h3 className="font-heading font-semibold">GST position</h3>
          <p className="text-xs text-muted-foreground mb-4">This quarter · {q.label}</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={[
              { name: "GST", collected: bas?.["1A_gst_on_sales"] || 0, paid: bas?.["1B_gst_on_purchases"] || 0 },
            ]}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2DDD3" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#94A3B8" />
              <YAxis tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} stroke="#94A3B8" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 12, border: "1px solid #E2DDD3" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="collected" fill="#0284C7" name="1A Collected" radius={[6, 6, 0, 0]} />
              <Bar dataKey="paid" fill="#D97706" name="1B Paid" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-3 rounded-lg bg-secondary px-4 py-3 flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Net GST (8A)</span>
            <span className={`num font-bold ${bas?.result === "payable" ? "text-rose-600" : "text-emerald-600"}`}>
              {money(bas?.amount)} {bas?.result === "payable" ? "payable" : "refund"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
